import {
  afterNextRender,
  Directive,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { PlatformContextService } from '@shared/services/platform-context';
import { Rellax as TypeRellax, RellaxInstance } from 'rellax';

declare let Rellax: TypeRellax;

@Directive({
  selector: '[parallax]',
  standalone: true,
})
export class ParallaxDirective implements OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly documentElement = inject(DOCUMENT).documentElement;
  private rellaxInstance: RellaxInstance | null = null;
  private readonly scrollBlocked: WritableSignal<boolean> = signal(false);
  private readonly isServer = inject(PlatformContextService).isServer;
  private observer: MutationObserver | null = null;

  constructor() {
    if (this.isServer()) {
      return;
    }

    effect(() => {
      const blocked = this.scrollBlocked();

      if (blocked) {
        this.destroyRellax();
      } else {
        this.createRellax();
      }
    });

    afterNextRender(() => {
      // Observer: track any mutation on <html class="">
      this.observer = new MutationObserver(() => {
        queueMicrotask(() => {
          const current = this.isScrollBlocked();
          if (current !== this.scrollBlocked()) {
            this.scrollBlocked.set(current);
          }
        });
      });

      this.observer.observe(this.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.destroyRellax();
  }

  private isScrollBlocked(): boolean {
    return this.documentElement.classList.contains('cdk-global-scrollblock');
  }

  private createRellax(): void {
    if (!this.rellaxInstance) {
      const el = this.el.nativeElement as HTMLElement;
      this.rellaxInstance = new Rellax(el, { center: true });
    }
  }

  private destroyRellax(): void {
    if (this.rellaxInstance) {
      this.rellaxInstance.destroy();
      this.rellaxInstance = null;
    }
  }
}
